import {
  Card,
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { StatusRow } from "./StatusRow";
import { ConsumerTable } from "./ConsumerTable";
import { ActiveJobsTable } from "./ActiveJobsTable";
import { PendingJobsTable } from "./PendingJobsTable";
import {
  useAutomationIntegrationDeploymentGetConsumerInfoQuery,
  useAutomationIntegrationDeploymentGetActiveJobsEndpointQuery,
  useAutomationIntegrationDeploymentGetPendingJobsEndpointQuery,
  type ConsumerInfoOut,
  type PendingJobOut,
} from "@providers/hop-ord-server/api";
import { useState, useEffect, useRef } from "react";

export const JobsOverviewCard = () => {
  const [lastConsumerRefresh, setLastConsumerRefresh] = useState<Date>(
    new Date(),
  );
  const [lastActiveJobsRefresh, setLastActiveJobsRefresh] = useState<Date>(
    new Date(),
  );
  const [lastPendingJobsRefresh, setLastPendingJobsRefresh] = useState<Date>(
    new Date(),
  );
  const consumerRefreshTriggered = useRef(false);
  const activeJobsRefreshTriggered = useRef(false);
  const pendingJobsRefreshTriggered = useRef(false);

  const {
    data: consumerInfoData,
    isLoading: consumerInfoLoading,
    error: consumerInfoError,
    refetch: refetchConsumerInfo,
    isFetching: consumerInfoFetching,
  } = useAutomationIntegrationDeploymentGetConsumerInfoQuery();

  const {
    data: activeJobsData,
    isLoading: activeJobsLoading,
    error: activeJobsError,
    refetch: refetchActiveJobs,
    isFetching: activeJobsFetching,
  } = useAutomationIntegrationDeploymentGetActiveJobsEndpointQuery();

  const {
    data: pendingJobsData,
    isLoading: pendingJobsLoading,
    error: pendingJobsError,
    refetch: refetchPendingJobs,
    isFetching: pendingJobsFetching,
  } = useAutomationIntegrationDeploymentGetPendingJobsEndpointQuery();

  // Derived state
  const consumerInfo = consumerInfoData || [];
  const activeJobs = activeJobsData || [];
  const pendingJobs = pendingJobsData || [];

  // Calculate summary stats
  const totalConsumers = consumerInfo.length;
  const totalPendingJobs = pendingJobs.reduce(
    (sum: number, job: PendingJobOut) => sum + job.pendingCount,
    0,
  );

  const handleConsumerRefresh = () => {
    consumerRefreshTriggered.current = true;
    refetchConsumerInfo();
  };

  const handleActiveJobsRefresh = () => {
    activeJobsRefreshTriggered.current = true;
    refetchActiveJobs();
  };

  const handlePendingJobsRefresh = () => {
    pendingJobsRefreshTriggered.current = true;
    refetchPendingJobs();
  };

  // Update timestamps when responses are received after manual refresh
  useEffect(() => {
    if (!consumerInfoFetching && consumerRefreshTriggered.current) {
      setLastConsumerRefresh(new Date());
      consumerRefreshTriggered.current = false;
    }
  }, [consumerInfoFetching]);

  useEffect(() => {
    if (!activeJobsFetching && activeJobsRefreshTriggered.current) {
      setLastActiveJobsRefresh(new Date());
      activeJobsRefreshTriggered.current = false;
    }
  }, [activeJobsFetching]);

  useEffect(() => {
    if (!pendingJobsFetching && pendingJobsRefreshTriggered.current) {
      setLastPendingJobsRefresh(new Date());
      pendingJobsRefreshTriggered.current = false;
    }
  }, [pendingJobsFetching]);

  // Group consumers by group name for better display
  const consumerGroups = consumerInfo.reduce(
    (groups: Record<string, ConsumerInfoOut[]>, consumer: ConsumerInfoOut) => {
      const group = groups[consumer.groupName] || [];
      group.push(consumer);
      groups[consumer.groupName] = group;
      return groups;
    },
    {} as Record<string, ConsumerInfoOut[]>,
  );

  return (
    <Card>
      <Stack padding={3} gap={3}>
        {/* Generation Jobs Status */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h3">
            Generation Jobs Status
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Updated: {lastConsumerRefresh.toLocaleTimeString()}
            </Typography>
            <Tooltip title="Refresh consumer info">
              <IconButton
                onClick={handleConsumerRefresh}
                disabled={consumerInfoFetching}
                color="primary"
                size="small"
              >
                {consumerInfoFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Refresh fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip
            label={`${totalConsumers} Total Consumers`}
            color="info"
            variant="outlined"
          />
          <Chip
            label={`${totalPendingJobs} Pending Jobs`}
            color={totalPendingJobs > 0 ? "warning" : "default"}
            variant="outlined"
          />
        </Box>

        {/* Consumer Information by Group */}
        {consumerInfoLoading ? (
          <StatusRow
            colSpan={6}
            message="Loading consumer information..."
            type="loading"
          />
        ) : consumerInfoError ? (
          <StatusRow
            colSpan={6}
            message="Error loading consumer information"
            type="error"
          />
        ) : (
          Object.entries(consumerGroups).map(
            ([groupName, consumers]: [string, ConsumerInfoOut[]]) => (
              <Box key={groupName}>
                <Typography variant="subtitle1" gutterBottom>
                  {groupName} ({consumers.length} consumers)
                </Typography>
                <ConsumerTable consumers={consumers} />
              </Box>
            ),
          )
        )}

        <Divider />

        {/* Active Jobs Details */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6" component="h3">
              Active Jobs ({activeJobs.length} active)
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Updated: {lastActiveJobsRefresh.toLocaleTimeString()}
              </Typography>
              <Tooltip title="Refresh active jobs">
                <IconButton
                  onClick={handleActiveJobsRefresh}
                  disabled={activeJobsFetching}
                  color="primary"
                  size="small"
                >
                  {activeJobsFetching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Refresh fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {activeJobsLoading ? (
            <StatusRow
              colSpan={7}
              message="Loading active jobs..."
              type="loading"
            />
          ) : activeJobsError ? (
            <StatusRow
              colSpan={7}
              message="Error loading active jobs"
              type="error"
            />
          ) : (
            <ActiveJobsTable activeJobs={activeJobs} />
          )}
        </Box>

        <Divider />

        {/* Pending Jobs Details */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6" component="h3">
              Pending Jobs ({totalPendingJobs} pending)
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Updated: {lastPendingJobsRefresh.toLocaleTimeString()}
              </Typography>
              <Tooltip title="Refresh pending jobs">
                <IconButton
                  onClick={handlePendingJobsRefresh}
                  disabled={pendingJobsFetching}
                  color="primary"
                  size="small"
                >
                  {pendingJobsFetching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Refresh fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {pendingJobsLoading ? (
            <StatusRow
              colSpan={7}
              message="Loading pending jobs..."
              type="loading"
            />
          ) : pendingJobsError ? (
            <StatusRow
              colSpan={7}
              message="Error loading pending jobs"
              type="error"
            />
          ) : (
            <PendingJobsTable pendingJobs={pendingJobs} />
          )}
        </Box>
      </Stack>
    </Card>
  );
};
